import type {GroupSingleOption, OptionGroups, OptionTab} from '@pb/kit-component-modal-options';
import type {SearchTemplateColumn, SortRowData} from 'src/modules/advanced-search-result/typedef';

export const SELECTION_KEY = 'account-search-result';
export const PLACE = 'Account Search Result';

export const SORTING_WARNING = 'The maximum number of suggestions allowed for sorting is 5000. Please clarify' +
    ' your criteria';
export const FILTERING_WARNING = 'The maximum number of suggestions allowed for filtering is 5000. Please clarify' +
    ' your criteria';
export const SORTING_ERROR = 'The number of suggestions exceeds the maximum allowed. Please clarify your criteria';

export const COLUMNS: Record<string, string> = {
    ACCOUNT_NAME: 'accountName',
    ACCOUNT_SEARCH_ID: 'accountId',
    ACCOUNT_STATUS: 'accountStatus',
    ENTITY_TYPE: 'entityType',
    ACCOUNT_COUNTRY: 'country',
    IS_ACCOUNT_DELETED: 'isDeleted',
    PARENT_ACCOUNT_NAME: 'parentAccountId',
    LICENSED_ENABLED_USERS: 'licensedEnabledUsers',
    MOBILE_ENABLED_USERS: 'mobileEnabledUsers',
    CRM_ENABLED_USERS: 'crmEnabledUsers',
    DISABLED_NEWSLETTER_USERS: 'disabledNewsletterUsers',
    NO_ACCESS_USERS: 'noAccessUsers',
    CONFERENCE_USERS: 'conferenceUsers',
    CREDIT_ALERTS_ENABLED_USERS: 'creditAlertsEnabledUsers',
    SALESFORCE_INTEGRATION: 'salesforceIntegration',
    MS_DYNAMICS_INTEGRATION: 'msDynamicsIntegration',
    DEAL_CLOUD_INTEGRATION: 'dealCloudIntegration',
    HUBSPOT_INTEGRATION: 'hubspotIntegration',
    SSO_ENABLED: 'ssoEnabled',
    SSO_MANDATORY: 'ssoMandatory',
    PRIMARY_ACCOUNT_MANAGER: 'primaryAccountManager',
    LICENSE_TYPE: 'licenseType',
    LICENSE_START_DATE: 'licenseStartDate',
    LICENSE_END_DATE: 'licenseEndDate',
    LICENSE_SEATS: 'licenseSeats',
};

export const ACCOUNT_SEARCH_COLUMNS: SearchTemplateColumn[] = [
    {code: COLUMNS.ACCOUNT_NAME, name: 'Account Name', required: true},
    {code: COLUMNS.ACCOUNT_SEARCH_ID, name: 'ID', required: false},
    {code: COLUMNS.ACCOUNT_STATUS, name: 'Account Status', required: false},
    {code: COLUMNS.ENTITY_TYPE, name: 'Entity Type', required: false},
    {code: COLUMNS.ACCOUNT_COUNTRY, name: 'Country', required: false},
    {code: COLUMNS.IS_ACCOUNT_DELETED, name: 'Deleted', required: false},
    {code: COLUMNS.PARENT_ACCOUNT_NAME, name: 'Parent Account', required: false},
    {code: COLUMNS.LICENSED_ENABLED_USERS, name: 'Licensed Enabled Users', required: false},
    {code: COLUMNS.MOBILE_ENABLED_USERS, name: 'Mobile Enabled Users', required: false},
    {code: COLUMNS.CRM_ENABLED_USERS, name: 'CRM Enabled Users', required: false},
    {code: COLUMNS.DISABLED_NEWSLETTER_USERS, name: 'Disabled (Newsletter)', required: false},
    {code: COLUMNS.NO_ACCESS_USERS, name: 'No Access Empty', required: false},
    {code: COLUMNS.CONFERENCE_USERS, name: 'Conference Users', required: false},
    {code: COLUMNS.CREDIT_ALERTS_ENABLED_USERS, name: 'Credit Alerts Enabled Users', required: false},
    {code: COLUMNS.SALESFORCE_INTEGRATION, name: 'Salesforce Integration', required: false},
    {code: COLUMNS.MS_DYNAMICS_INTEGRATION, name: 'MS Dynamics Integration', required: false},
    {code: COLUMNS.DEAL_CLOUD_INTEGRATION, name: 'DealCloud Integration', required: false},
    {code: COLUMNS.HUBSPOT_INTEGRATION, name: 'HubSpot Integration', required: false},
    {code: COLUMNS.SSO_ENABLED, name: 'SSO Enabled', required: false},
    {code: COLUMNS.SSO_MANDATORY, name: 'SSO Mandatory', required: false},
    {code: COLUMNS.PRIMARY_ACCOUNT_MANAGER, name: 'Primary Account Team Member', required: false},
    {code: COLUMNS.LICENSE_TYPE, name: 'License Type', required: false},
    {code: COLUMNS.LICENSE_START_DATE, name: 'License Start Date', required: false},
    {code: COLUMNS.LICENSE_END_DATE, name: 'License End Date', required: false},
    {code: COLUMNS.LICENSE_SEATS, name: 'License Seats', required: false},
];

export const LOADING_PRIORITY: Array<{columnId: string, chunk: string, priority: number}> = [
    {chunk: 'crmIntegration', columnId: COLUMNS.SALESFORCE_INTEGRATION, priority: 4},
    {chunk: 'crmIntegration', columnId: COLUMNS.MS_DYNAMICS_INTEGRATION, priority: 3},
    {chunk: 'crmIntegration', columnId: COLUMNS.DEAL_CLOUD_INTEGRATION, priority: 2},
    {chunk: 'crmIntegration', columnId: COLUMNS.HUBSPOT_INTEGRATION, priority: 1},

    {chunk: 'license', columnId: COLUMNS.LICENSE_TYPE, priority: 4},
    {chunk: 'license', columnId: COLUMNS.LICENSE_START_DATE, priority: 3},
    {chunk: 'license', columnId: COLUMNS.LICENSE_END_DATE, priority: 2},
    {chunk: 'license', columnId: COLUMNS.LICENSE_SEATS, priority: 1},
];

export const LIMITED_SORT_COLUMNS = [
    COLUMNS.PARENT_ACCOUNT_NAME,
    COLUMNS.SALESFORCE_INTEGRATION,
    COLUMNS.MS_DYNAMICS_INTEGRATION,
    COLUMNS.DEAL_CLOUD_INTEGRATION,
    COLUMNS.HUBSPOT_INTEGRATION,
    COLUMNS.SSO_ENABLED,
    COLUMNS.SSO_MANDATORY,
];

export const NO_SORT_COLUMNS = [
    COLUMNS.LICENSED_ENABLED_USERS,
    COLUMNS.MOBILE_ENABLED_USERS,
    COLUMNS.CRM_ENABLED_USERS,
    COLUMNS.DISABLED_NEWSLETTER_USERS,
    COLUMNS.NO_ACCESS_USERS,
    COLUMNS.CONFERENCE_USERS,
    COLUMNS.CREDIT_ALERTS_ENABLED_USERS,
];

const NUMBER_SORTING_LABELS: SortRowData = {
    ascLabel: 'Ascending',
    descLabel: 'Descending',
};

const TEXT_SORTING_LABELS: SortRowData = {
    ascLabel: 'A-Z',
    descLabel: 'Z-A',
};

const BOOLEAN_SORTING_LABELS: SortRowData = {
    ascLabel: 'No first',
    descLabel: 'Yes first',
};

const DATE_SORTING_LABELS: SortRowData = {
    ascLabel: 'Oldest to Newest',
    descLabel: 'Newest to Oldest',
};

export const SORING_LABElS: Record<string, SortRowData> = {
    [COLUMNS.ACCOUNT_NAME]: TEXT_SORTING_LABELS,
    [COLUMNS.ACCOUNT_SEARCH_ID]: NUMBER_SORTING_LABELS,
    [COLUMNS.ACCOUNT_STATUS]: TEXT_SORTING_LABELS,
    [COLUMNS.ENTITY_TYPE]: TEXT_SORTING_LABELS,
    [COLUMNS.ACCOUNT_COUNTRY]: TEXT_SORTING_LABELS,
    [COLUMNS.IS_ACCOUNT_DELETED]: BOOLEAN_SORTING_LABELS,
    [COLUMNS.PARENT_ACCOUNT_NAME]: TEXT_SORTING_LABELS,
    [COLUMNS.LICENSED_ENABLED_USERS]: NUMBER_SORTING_LABELS,
    [COLUMNS.MOBILE_ENABLED_USERS]: NUMBER_SORTING_LABELS,
    [COLUMNS.CRM_ENABLED_USERS]: NUMBER_SORTING_LABELS,
    [COLUMNS.DISABLED_NEWSLETTER_USERS]: NUMBER_SORTING_LABELS,
    [COLUMNS.NO_ACCESS_USERS]: NUMBER_SORTING_LABELS,
    [COLUMNS.CONFERENCE_USERS]: NUMBER_SORTING_LABELS,
    [COLUMNS.SALESFORCE_INTEGRATION]: BOOLEAN_SORTING_LABELS,
    [COLUMNS.MS_DYNAMICS_INTEGRATION]: BOOLEAN_SORTING_LABELS,
    [COLUMNS.DEAL_CLOUD_INTEGRATION]: BOOLEAN_SORTING_LABELS,
    [COLUMNS.HUBSPOT_INTEGRATION]: BOOLEAN_SORTING_LABELS,
    [COLUMNS.SSO_ENABLED]: BOOLEAN_SORTING_LABELS,
    [COLUMNS.SSO_MANDATORY]: BOOLEAN_SORTING_LABELS,
    [COLUMNS.PRIMARY_ACCOUNT_MANAGER]: TEXT_SORTING_LABELS,
    [COLUMNS.LICENSE_TYPE]: TEXT_SORTING_LABELS,
    [COLUMNS.LICENSE_START_DATE]: DATE_SORTING_LABELS,
    [COLUMNS.LICENSE_END_DATE]: DATE_SORTING_LABELS,
    [COLUMNS.LICENSE_SEATS]: NUMBER_SORTING_LABELS,
    [COLUMNS.CREDIT_ALERTS_ENABLED_USERS]: NUMBER_SORTING_LABELS,
};

export const OPTIONS_SETTINGS = {
    count_1: {
        size: 'Sizes.size-12',
        width: 's',
    },
    count_2: {
        size: 'Sizes.size-6',
        width: 's',
    },
    count_3: {
        size: 'Sizes.size-4',
        width: 'm',
    },
    count_4: {
        size: 'Sizes.size-3',
        width: 'm',
    },
};

export const OPTION_GROUPS: OptionGroups = {
    ACCOUNT_SEARCH_COLUMNS: [
        {options: ['accountDetails', 'crmIntegration', 'sso']},
        {options: ['accountUsers', 'accountTeam', 'license']},
    ],
};


export const OPTIONS_MAP: Record<string, GroupSingleOption> = {
    [COLUMNS.ACCOUNT_NAME]: {
        hidden: false,
        defaultOption: true,
        option: {
            label: 'Account Name',
            fixed: true,
            pinned: false,
            optionId: 'accountName',
        },
    },
    [COLUMNS.ACCOUNT_SEARCH_ID]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'ID',
            fixed: false,
            pinned: false,
            optionId: 'accountId',
        },
    },
    [COLUMNS.ACCOUNT_STATUS]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Account Status',
            fixed: false,
            pinned: false,
            optionId: 'accountStatus',
        },
    },
    [COLUMNS.ENTITY_TYPE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Entity Type',
            fixed: false,
            pinned: false,
            optionId: 'entityType',
        },
    },
    [COLUMNS.ACCOUNT_COUNTRY]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Country',
            fixed: false,
            pinned: false,
            optionId: 'country',
        },
    },
    [COLUMNS.IS_ACCOUNT_DELETED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Deleted',
            fixed: false,
            pinned: false,
            optionId: 'isDeleted',
        },
    },
    [COLUMNS.PARENT_ACCOUNT_NAME]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Parent Account',
            fixed: false,
            pinned: false,
            optionId: 'parentAccountId',
        },
    },
    [COLUMNS.LICENSED_ENABLED_USERS]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Licensed Enabled Users',
            fixed: false,
            pinned: false,
            optionId: 'licensedEnabledUsers',
        },
    },
    [COLUMNS.MOBILE_ENABLED_USERS]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Mobile Enabled Users',
            fixed: false,
            pinned: false,
            optionId: 'mobileEnabledUsers',
        },
    },
    [COLUMNS.CRM_ENABLED_USERS]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'CRM Enabled Users',
            fixed: false,
            pinned: false,
            optionId: 'crmEnabledUsers',
        },
    },
    [COLUMNS.DISABLED_NEWSLETTER_USERS]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Disabled (Newsletter)',
            fixed: false,
            pinned: false,
            optionId: 'disabledNewsletterUsers',
        },
    },
    [COLUMNS.NO_ACCESS_USERS]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'No Access Empty',
            fixed: false,
            pinned: false,
            optionId: 'noAccessUsers',
        },
    },
    [COLUMNS.CONFERENCE_USERS]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Conference Users',
            fixed: false,
            pinned: false,
            optionId: 'conferenceUsers',
        },
    },
    [COLUMNS.CREDIT_ALERTS_ENABLED_USERS]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Credit Alerts Enabled Users',
            fixed: false,
            pinned: false,
            optionId: 'creditAlertsEnabledUsers',
        },
    },
    [COLUMNS.SALESFORCE_INTEGRATION]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Salesforce Integration',
            fixed: false,
            pinned: false,
            optionId: 'salesforceIntegration',
        },
    },
    [COLUMNS.MS_DYNAMICS_INTEGRATION]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'MS Dynamics Integration',
            fixed: false,
            pinned: false,
            optionId: 'msDynamicsIntegration',
        },
    },
    [COLUMNS.DEAL_CLOUD_INTEGRATION]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'DealCloud Integration',
            fixed: false,
            pinned: false,
            optionId: 'dealCloudIntegration',
        },
    },
    [COLUMNS.HUBSPOT_INTEGRATION]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'HubSpot Integration',
            fixed: false,
            pinned: false,
            optionId: 'hubspotIntegration',
        },
    },
    [COLUMNS.SSO_ENABLED]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'SSO Enabled',
            fixed: false,
            pinned: false,
            optionId: 'ssoEnabled',
        },
    },
    [COLUMNS.SSO_MANDATORY]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'SSO Mandatory',
            fixed: false,
            pinned: false,
            optionId: 'ssoMandatory',
        },
    },
    [COLUMNS.PRIMARY_ACCOUNT_MANAGER]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'Primary Account Team Member',
            fixed: false,
            pinned: false,
            optionId: 'primaryAccountManager',
        },
    },
    [COLUMNS.LICENSE_TYPE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'License Type',
            fixed: false,
            pinned: false,
            optionId: 'licenseType',
        },
    },
    [COLUMNS.LICENSE_START_DATE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'License Start Date',
            fixed: false,
            pinned: false,
            optionId: 'licenseStartDate',
        },
    },
    [COLUMNS.LICENSE_END_DATE]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'License End Date',
            fixed: false,
            pinned: false,
            optionId: 'licenseEndDate',
        },
    },
    [COLUMNS.LICENSE_SEATS]: {
        hidden: false,
        defaultOption: false,
        option: {
            label: 'License Seats',
            fixed: false,
            pinned: false,
            optionId: 'licenseSeats',
        },
    },
};

export const ALL_OPTIONS: GroupSingleOption[] = Object.values(OPTIONS_MAP);

export const ITEMS: OptionTab[] = [
    {
        tabId: 'ACCOUNT_SEARCH_COLUMNS',
        caption: 'Account Search Columns',
        items: [
            {
                id: 'accountDetails',
                caption: 'Account Details',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.ACCOUNT_NAME],
                    OPTIONS_MAP[COLUMNS.ACCOUNT_SEARCH_ID],
                    OPTIONS_MAP[COLUMNS.ACCOUNT_STATUS],
                    OPTIONS_MAP[COLUMNS.ENTITY_TYPE],
                    OPTIONS_MAP[COLUMNS.ACCOUNT_COUNTRY],
                    OPTIONS_MAP[COLUMNS.IS_ACCOUNT_DELETED],
                    OPTIONS_MAP[COLUMNS.PARENT_ACCOUNT_NAME],
                ],
            },
            {
                id: 'accountUsers',
                caption: 'Users',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.LICENSED_ENABLED_USERS],
                    OPTIONS_MAP[COLUMNS.MOBILE_ENABLED_USERS],
                    OPTIONS_MAP[COLUMNS.CRM_ENABLED_USERS],
                    OPTIONS_MAP[COLUMNS.DISABLED_NEWSLETTER_USERS],
                    OPTIONS_MAP[COLUMNS.NO_ACCESS_USERS],
                    OPTIONS_MAP[COLUMNS.CONFERENCE_USERS],
                    OPTIONS_MAP[COLUMNS.CREDIT_ALERTS_ENABLED_USERS],
                ],
            },
            {
                id: 'crmIntegration',
                caption: 'CRM Integration',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.SALESFORCE_INTEGRATION],
                    OPTIONS_MAP[COLUMNS.MS_DYNAMICS_INTEGRATION],
                    OPTIONS_MAP[COLUMNS.DEAL_CLOUD_INTEGRATION],
                    OPTIONS_MAP[COLUMNS.HUBSPOT_INTEGRATION],
                ],
            },
            {
                id: 'sso',
                caption: 'SSO',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.SSO_ENABLED],
                    OPTIONS_MAP[COLUMNS.SSO_MANDATORY],
                ],
            },
            {
                id: 'accountTeam',
                caption: 'Account Team',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.PRIMARY_ACCOUNT_MANAGER],
                ],
            },
            {
                id: 'license',
                caption: 'Active License',
                collapsible: false,
                options: [
                    OPTIONS_MAP[COLUMNS.LICENSE_TYPE],
                    OPTIONS_MAP[COLUMNS.LICENSE_START_DATE],
                    OPTIONS_MAP[COLUMNS.LICENSE_END_DATE],
                    OPTIONS_MAP[COLUMNS.LICENSE_SEATS],
                ],
            },
        ],
        hidden: true,
    },
];

export const HEADERS = Object.fromEntries(
    ACCOUNT_SEARCH_COLUMNS.map(({code, name}) => [code, name]),
);

export const COLUMNS_DATA = [
    {
        columnId: COLUMNS.ACCOUNT_NAME,
        fixed: true,
        defaultWidth: 324,
        minWidth: 324,
        maxWidth: 520,
    },
    {
        columnId: COLUMNS.ACCOUNT_SEARCH_ID,
        fixed: false,
        defaultWidth: 200,
        minWidth: 100,
        maxWidth: 300,
    },
    {
        columnId: COLUMNS.ACCOUNT_STATUS,
        fixed: false,
        defaultWidth: 200,
        minWidth: 200,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.ENTITY_TYPE,
        fixed: false,
        defaultWidth: 200,
        minWidth: 200,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.ACCOUNT_COUNTRY,
        fixed: false,
        defaultWidth: 200,
        minWidth: 200,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.DELETED,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 250,
    },
    {
        columnId: COLUMNS.PARENT_ACCOUNT_NAME,
        fixed: false,
        defaultWidth: 200,
        minWidth: 200,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.LICENSED_ENABLED_USERS,
        fixed: false,
        defaultWidth: 250,
        minWidth: 200,
        maxWidth: 300,
    },
    {
        columnId: COLUMNS.MOBILE_ENABLED_USERS,
        fixed: false,
        defaultWidth: 250,
        minWidth: 200,
        maxWidth: 300,
    },
    {
        columnId: COLUMNS.CRM_ENABLED_USERS,
        fixed: false,
        defaultWidth: 250,
        minWidth: 200,
        maxWidth: 300,
    },
    {
        columnId: COLUMNS.DISABLED_NEWSLETTER_USERS,
        fixed: false,
        defaultWidth: 250,
        minWidth: 200,
        maxWidth: 300,
    },
    {
        columnId: COLUMNS.NO_ACCESS_USERS,
        fixed: false,
        defaultWidth: 250,
        minWidth: 200,
        maxWidth: 300,
    },
    {
        columnId: COLUMNS.CONFERENCE_USERS,
        fixed: false,
        defaultWidth: 250,
        minWidth: 200,
        maxWidth: 300,
    },
    {
        columnId: COLUMNS.CREDIT_ALERTS_ENABLED_USERS,
        fixed: false,
        defaultWidth: 250,
        minWidth: 200,
        maxWidth: 300,
    },
    {
        columnId: COLUMNS.SALESFORCE_INTEGRATION,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 250,
    },
    {
        columnId: COLUMNS.MS_DYNAMICS_INTEGRATION,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 250,
    },
    {
        columnId: COLUMNS.DEAL_CLOUD_INTEGRATION,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 250,
    },
    {
        columnId: COLUMNS.HUBSPOT_INTEGRATION,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 250,
    },
    {
        columnId: COLUMNS.SSO_ENABLED,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 250,
    },
    {
        columnId: COLUMNS.SSO_MANDATORY,
        fixed: false,
        defaultWidth: 200,
        minWidth: 150,
        maxWidth: 250,
    },
    {
        columnId: COLUMNS.PRIMARY_ACCOUNT_MANAGER,
        fixed: false,
        defaultWidth: 300,
        minWidth: 200,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.LICENSE_TYPE,
        fixed: false,
        defaultWidth: 200,
        minWidth: 200,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.LICENSE_START_DATE,
        fixed: false,
        defaultWidth: 200,
        minWidth: 200,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.LICENSE_END_DATE,
        fixed: false,
        defaultWidth: 200,
        minWidth: 200,
        maxWidth: 400,
    },
    {
        columnId: COLUMNS.LICENSE_SEATS,
        fixed: false,
        defaultWidth: 200,
        minWidth: 200,
        maxWidth: 400,
    },
];
